/**
 * Five9 integration service (Plane A — server-side data; no Five9 SDK involved).
 *
 * Today this covers screen-pop contact resolution only. Encounter records and
 * recording/transcript links follow once ingestion is activated.
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

/** One candidate contact for an inbound ANI. Identifying fields only — deliberately no
 *  membership, financial or address data crosses this boundary. */
export interface Five9PhoneCandidate {
  id: UUID;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  lifecycle_stage: string | null;
  status: string | null;
}

/**
 * `matched`    — exactly one active unmerged contact. Safe to pop.
 * `ambiguous`  — several. The resolver NEVER tie-breaks (scope §3.3); a human picks.
 * `unmatched`  — nobody, or the ANI is unusable. See `reason`.
 */
export type Five9PhoneResolution = "matched" | "ambiguous" | "unmatched";

export type Five9PhoneReason =
  | "no_match"
  /** Fewer than 10 digits — a withheld number or an internal extension. */
  | "ani_too_short"
  /** Shared by more than `distinctive_max` contacts, so it identifies nobody. On PROD this
   *  is placeholder data (0000000000 sits on 15,453 contacts) or a company line. */
  | "ani_not_distinctive"
  | "multiple_active_matches";

export interface Five9PhoneLookup {
  resolution: Five9PhoneResolution;
  reason?: Five9PhoneReason;
  contact_id?: UUID;
  /** The last 10 digits actually searched — shown to the operator so a mis-dial is obvious. */
  normalized: string;
  candidates: Five9PhoneCandidate[];
  match_count?: number;
  /** Present when the ANI is non-distinctive: a floor, not an exact count. */
  match_count_at_least?: number;
  /** True when there were more genuine matches than the candidate cap returned. */
  truncated?: boolean;
}

/**
 * Resolve an inbound phone number to a contact.
 *
 * Matches the last 10 digits against `phone_number` and `mobile_phone`, both of which have
 * functional indexes. `home_phone` / `work_phone` are NOT searched — no index exists and a
 * sequential scan over 2.5M rows is unacceptable on a path that runs while a caller waits.
 */
export const resolveContactByPhone = async (
  phone: string,
): Promise<Five9PhoneLookup> => {
  const { data, error } = await supabase.rpc("resolve_contact_by_phone", {
    p_phone: phone,
  });

  if (error) {
    console.error("[five9] resolve_contact_by_phone failed:", error);
    throw error;
  }

  // Defensive: a null/odd payload must present as "unmatched", never as a crash on a screen
  // an agent hits mid-call.
  const raw = (data ?? {}) as Partial<Five9PhoneLookup>;
  return {
    resolution: raw.resolution ?? "unmatched",
    reason: raw.reason,
    contact_id: raw.contact_id,
    normalized: raw.normalized ?? "",
    candidates: Array.isArray(raw.candidates) ? raw.candidates : [],
    match_count: raw.match_count,
    match_count_at_least: raw.match_count_at_least,
    truncated: raw.truncated,
  };
};

/**
 * Read an inbound phone number from a screen-pop URL's query string.
 *
 * Five9's variable name is not yet confirmed (question 1 to Five9), so several plausible
 * parameter names are accepted. Whichever they send, the pop works; once confirmed, the extra
 * aliases cost nothing and keep older configured URLs working.
 */
export const aniFromSearchParams = (params: URLSearchParams): string | null => {
  for (const key of [
    "ani",
    "phone",
    "callerid",
    "caller_id",
    "from",
    "number",
  ]) {
    const v = params.get(key);
    if (v && v.trim()) {
      return v.trim();
    }
  }
  return null;
};
